// RejoindreAsso.jsx — Inscription self-service via code d'association (/rejoindre/:code)
// Page publique brandée : un futur membre saisit ses infos + choisit une formule,
// crée son compte et arrive dans l'espace membre (adhésion en attente de validation du bureau).
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { applyTheme } from '@/lib/theme';
import { toast } from 'sonner';
import { Loader2, Gift, Coins, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';

function errMsg(e, fallback = 'Une erreur est survenue') {
  const d = e?.response?.data?.detail ?? e?.response?.data?.error;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ') || fallback;
  return fallback;
}

const FORMULE_META = {
  free_first: { icon: Gift, groupe: 'Découverte', hint: 'Sans engagement' },
  per_meeting: { icon: Coins, groupe: 'Participant', hint: 'Paiement à la réunion' },
  annual_1x: { icon: Sparkles, groupe: 'Adhérent', hint: 'Accès complet', reco: true },
  annual_3x: { icon: Sparkles, groupe: 'Adhérent', hint: '3× sans frais' },
};

export default function RejoindreAsso() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [asso, setAsso] = useState(null);
  const [tarif, setTarif] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', telephone: '', mot_de_passe: '' });
  const [formule, setFormule] = useState('annual_1x');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/rejoindre/${encodeURIComponent(code)}`);
        setAsso(data.asso);
        setTarif(data.tarification || {});
        if (data.asso?.couleur_principale) applyTheme(data.asso.couleur_principale);
        const keys = Object.keys(data.tarification || {});
        setFormule(keys.includes('annual_1x') ? 'annual_1x' : (keys[0] || 'free_first'));
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prenom || !form.nom || !form.email || form.mot_de_passe.length < 6) {
      return toast.error('Remplissez tous les champs (mot de passe : 6 caractères min.)');
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/rejoindre/${encodeURIComponent(code)}`, { ...form, formule });
      localStorage.setItem('assoperf_token', data.token);
      localStorage.setItem('assoperf_refresh', data.refresh_token);
      localStorage.setItem('assoperf_user', JSON.stringify(data.user));
      setUser(data.user);
      toast.success(data.adhesion_statut === 'active'
        ? 'Bienvenue ! Votre adhésion est active.'
        : 'Compte créé ! Votre adhésion est en attente de validation du bureau.');
      navigate('/espace');
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#F9F9F8]"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div>;
  }
  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F9F9F8] p-8 text-center">
        <div>
          <h1 className="font-display text-2xl font-black text-[#0F172A] mb-2">Code introuvable</h1>
          <p className="text-sm text-[#475569]">Le code d'association « {code} » n'existe pas ou n'est plus actif.</p>
        </div>
      </div>
    );
  }

  const initials = (asso.nom || 'A').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const formuleKeys = Object.keys(tarif);

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-testid="rejoindre-page">
      {/* Header brandé */}
      <div className="text-white px-4 py-10 sm:py-14" style={{ background: 'var(--theme-primary)' }}>
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          {asso.logo_url ? (
            <img src={asso.logo_url} alt="" className="w-14 h-14 rounded-2xl object-cover bg-white/20" />
          ) : (
            <div className="w-14 h-14 rounded-2xl grid place-items-center bg-white/20 font-display font-black text-xl">{initials}</div>
          )}
          <div>
            <div className="text-[10px] font-black tracking-[0.18em] opacity-90">REJOINDRE</div>
            <h1 className="font-display text-2xl sm:text-3xl font-black">{asso.nom}</h1>
            {asso.ville && <div className="text-sm opacity-90">{asso.ville}</div>}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={submit} className="grid gap-6" data-testid="rejoindre-form">
          {/* Choix formule */}
          <div>
            <h2 className="font-display text-lg font-bold text-[#0F172A] mb-3">1. Choisissez votre formule</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {formuleKeys.map((key) => {
                const t = tarif[key];
                const meta = FORMULE_META[key] || { icon: Sparkles, groupe: '', hint: '' };
                const active = formule === key;
                return (
                  <button
                    type="button" key={key} onClick={() => setFormule(key)}
                    data-testid={`rejoindre-formule-${key}`}
                    className={`card text-left transition-all ${active ? '!border-2' : 'hover:border-[#CBD5E1]'}`}
                    style={active ? { borderColor: 'var(--theme-primary)' } : {}}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
                        <meta.icon className="w-4 h-4" />
                      </div>
                      {active && <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />}
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-wider text-[#94A3B8] mt-2">{meta.groupe}</div>
                    <div className="font-display font-black text-[#0F172A]">{t.label}</div>
                    <div className="font-display text-xl font-black" style={{ color: 'var(--theme-primary)' }}>
                      {t.prix_cts === 0 ? 'Gratuit' : `${(t.prix_cts / 100).toFixed(0)} €`}
                      {t.count && <span className="text-xs text-[#475569] font-normal"> × {t.count}</span>}
                    </div>
                    <div className="text-[11px] text-[#475569]">{meta.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Infos perso */}
          <div>
            <h2 className="font-display text-lg font-bold text-[#0F172A] mb-3">2. Vos informations</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} testid="rejoindre-prenom" />
              <Field label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} testid="rejoindre-nom" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="rejoindre-email" />
              <Field label="Téléphone (optionnel)" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="rejoindre-telephone" />
              <div className="sm:col-span-2">
                <Field label="Mot de passe (6 caractères min.)" type="password" value={form.mot_de_passe} onChange={(v) => setForm({ ...form, mot_de_passe: v })} testid="rejoindre-password" />
              </div>
            </div>
          </div>

          <div className="text-xs text-[#475569] bg-white border border-[#E2E8F0] rounded-xl px-4 py-3">
            Après validation de votre demande par le bureau, vous pourrez régler votre adhésion (chèque, espèces ou carte).
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="rejoindre-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Créer mon compte <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</span>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2"
        style={{ '--tw-ring-color': 'var(--theme-primary)' }}
      />
    </label>
  );
}
